'use client';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, Loader2, AlertCircle } from '@/components/ui/icons';
import { safePlay } from '@/lib/safePlay';
import { useVideoFullscreen } from './videoFullscreen';
function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}
interface LearnVideoPlayerProps {
    src: string;
    title?: string;
}
export function LearnVideoPlayer({ src, title }: LearnVideoPlayerProps) {
    const t = useTranslations$1();
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showControls, setShowControls] = useState(true);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const { strategy: fullscreenStrategy, isFullscreen, toggleFullscreen } = useVideoFullscreen(containerRef, videoRef);
    // Show controls and schedule the auto-hide. A timeout firing while paused is
    // harmless — visibility is derived below, and pause always shows controls.
    const resetHideTimer = useCallback(()=>{
        setShowControls(true);
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(()=>setShowControls(false), 2500);
    }, []);
    useEffect(()=>()=>clearTimeout(hideTimeoutRef.current), []);
    const controlsVisible = showControls || !isPlaying;
    const togglePlay = useCallback(()=>{
        const video = videoRef.current;
        if (!video) return;
        if (isPlaying) {
            video.pause();
        } else {
            safePlay(video);
        }
    }, [
        isPlaying
    ]);
    const toggleMute = useCallback(()=>{
        const video = videoRef.current;
        if (!video) return;
        video.muted = !isMuted;
        setIsMuted(!isMuted);
    }, [
        isMuted
    ]);
    const handleTimeUpdate = useCallback(()=>{
        const video = videoRef.current;
        if (!video || !video.duration) return;
        setProgress((video.currentTime / video.duration) * 100);
        setCurrentTime(video.currentTime);
    }, []);
    const handleLoadedMetadata = useCallback(()=>{
        const video = videoRef.current;
        if (!video) return;
        setDuration(video.duration);
    }, []);
    const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>)=>{
        const video = videoRef.current;
        const bar = progressRef.current;
        if (!video || !bar || !video.duration) return;
        const rect = bar.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        video.currentTime = pct * video.duration;
    }, []);
    useEffect(()=>{
        const container = containerRef.current;
        if (!container) return;
        const handleKey = (e: KeyboardEvent)=>{
            if (e.code === 'Space') {
                e.preventDefault();
                togglePlay();
            }
        };
        container.addEventListener('keydown', handleKey);
        return ()=>container.removeEventListener('keydown', handleKey);
    }, [
        togglePlay
    ]);
    return (<div ref={containerRef} tabIndex={0} className="relative overflow-hidden rounded-lg bg-neutral-900 outline-none" onMouseMove={resetHideTimer} onMouseLeave={()=>isPlaying && setShowControls(false)}>
      <div className="relative aspect-video">
        {isLoading && !hasError && (<div className="absolute inset-0 z-10 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white/40"/>
          </div>)}

        {hasError && (<div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3">
            <AlertCircle className="h-8 w-8 text-muted-foreground"/>
            <p className="text-sm text-muted-foreground">{t("NJGASf")}</p>
            <button onClick={()=>{
        setHasError(false);
        setIsLoading(true);
        videoRef.current?.load();
    }} className="text-sm text-white/60 underline underline-offset-2 transition-colors hover:text-white">
              {t("62nsdy")}
            </button>
          </div>)}

        <video ref={videoRef} src={src} playsInline preload="metadata" className="absolute inset-0 h-full w-full object-contain" aria-label={title} onClick={togglePlay} onCanPlay={()=>setIsLoading(false)} onLoadedMetadata={handleLoadedMetadata} onPlay={()=>{
        setIsPlaying(true);
        resetHideTimer();
    }} onPause={()=>setIsPlaying(false)} onTimeUpdate={handleTimeUpdate} onEnded={()=>setIsPlaying(false)} onError={()=>{
        setHasError(true);
        setIsLoading(false);
    }}/>

        {!hasError && (<div className={`absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-12 transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}>
            <div ref={progressRef} className="group/bar mb-3 flex h-4 cursor-pointer items-end" onClick={handleSeek}>
              <div className="h-1 w-full rounded-full bg-white/20 group-hover/bar:h-1.5">
                <div className="h-full rounded-full bg-white" style={{
        width: `${progress}%`
    }}/>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={togglePlay} className="text-white transition-colors hover:text-white/80" aria-label={isPlaying ? t("tFFMkF") : t("J3ca41")}>
                {isPlaying ? <Pause className="h-4 w-4"/> : <Play className="h-4 w-4" fill="currentColor"/>}
              </button>

              <button onClick={toggleMute} className="text-white transition-colors hover:text-white/80" aria-label={isMuted ? t("W9355R") : t("x82IOl")}>
                {isMuted ? <VolumeX className="h-4 w-4"/> : <Volume2 className="h-4 w-4"/>}
              </button>

              <span className="text-xs tabular-nums text-white/70">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              {fullscreenStrategy && (<div className="ms-auto">
                  <button onClick={toggleFullscreen} className="text-white transition-colors hover:text-white/80" aria-label={isFullscreen ? t("3OrdgP") : t("zvKOAu")}>
                    {isFullscreen ? <Minimize className="h-4 w-4"/> : <Maximize className="h-4 w-4"/>}
                  </button>
                </div>)}
            </div>
          </div>)}
      </div>
    </div>);
}
